<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import UploadField from "@/components/UploadField.vue";
import { uploadImage, uploadVideo } from "@/lib/uploads";
import { fetchCampaignVideo, addCampaignVideo, editCampaignVideo } from "./api";

const route = useRoute(), router = useRouter();
const id = computed(() => route.params.id || null);
const isEdit = computed(() => !!id.value);
const loading = ref(false), notFound = ref(false), saving = ref(false), success = ref(false), error = ref(""), validationError = ref("");
let original = null;

const title = ref(""), sequence = ref(""), enable = ref(false), imageUrl = ref(""), videoUrl = ref(""), duration = ref("");
const imageUploader = (file, p) => uploadImage("videos", file, p);
const videoUploader = (file, p) => uploadVideo(file, p);

function readDuration(file) {
  const el = document.createElement("video");
  el.preload = "metadata";
  el.onloadedmetadata = () => {
    URL.revokeObjectURL(el.src);
    const t = Math.round(el.duration || 0);
    duration.value = `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
  };
  el.src = URL.createObjectURL(file);
}

onMounted(async () => {
  if (!isEdit.value) return;
  loading.value = true;
  try {
    const v = await fetchCampaignVideo(id.value);
    if (!v) { notFound.value = true; return; }
    original = v; title.value = v.title ?? ""; sequence.value = v.sequence ?? ""; enable.value = !!v.enable; imageUrl.value = v.imageUrl ?? ""; videoUrl.value = v.videoUrl ?? ""; duration.value = v.duration ?? "";
  } catch { error.value = "Couldn't load the video."; }
  finally { loading.value = false; }
});

async function save() {
  success.value = false; error.value = ""; validationError.value = "";
  if (!title.value.trim()) { validationError.value = "Enter a title."; return; }
  if (!videoUrl.value) { validationError.value = "Upload a video clip."; return; }
  const payload = { ...(original || {}), title: title.value.trim(), imageUrl: imageUrl.value, videoUrl: videoUrl.value, duration: duration.value, sequence: sequence.value === "" ? null : Number(sequence.value), enable: enable.value };
  saving.value = true;
  try {
    if (isEdit.value) await editCampaignVideo(id.value, payload); else await addCampaignVideo(payload);
    success.value = true; setTimeout(() => router.push({ name: "campaign-videos" }), 800);
  } catch { error.value = "Couldn't save. Please try again."; }
  finally { saving.value = false; }
}
</script>

<template>
  <section>
    <VBtn variant="text" prepend-icon="mdi:arrow-left" class="mb-4" @click="router.push({ name: 'campaign-videos' })">Back</VBtn>
    <h1 class="text-h5 mb-4">{{ isEdit ? "Edit Campaign Video" : "Add Campaign Video" }}</h1>
    <p v-if="loading">Loading…</p>
    <VAlert v-else-if="notFound" type="error">Video not found.</VAlert>
    <VCard v-else class="pa-4" max-width="720">
      <VAlert v-if="error" type="error" class="mb-4">{{ error }}</VAlert>
      <VAlert v-if="success" type="success" class="mb-4">Saved. Redirecting…</VAlert>
      <form @submit.prevent="save">
        <VRow>
          <VCol cols="12"><VTextField v-model="title" label="Title" /></VCol>
          <VCol cols="12"><UploadField v-model="imageUrl" kind="image" label="Cover Image" :uploader="imageUploader" /></VCol>
          <VCol cols="12">
            <UploadField v-model="videoUrl" kind="video" label="Video Clip" :uploader="videoUploader" @select="readDuration" />
            <p v-if="duration" class="text-caption text-medium-emphasis mt-1">Duration: {{ duration }}</p>
          </VCol>
          <VCol cols="12" md="6"><VTextField v-model="sequence" label="Sequence" type="number" /></VCol>
          <VCol cols="12"><VCheckbox v-model="enable" label="Enabled" /></VCol>
        </VRow>
        <VAlert v-if="validationError" type="warning" class="mb-4">{{ validationError }}</VAlert>
        <div class="d-flex gap-2 mt-2">
          <VBtn type="submit" :loading="saving">{{ isEdit ? "Update" : "Add" }}</VBtn>
          <VBtn variant="tonal" @click="router.push({ name: 'campaign-videos' })">Cancel</VBtn>
        </div>
      </form>
    </VCard>
  </section>
</template>
